import { useRef, useLayoutEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import type { PaginationConfig } from './types'
import { TableFooter, type TableSurface } from './TableFooter'

/**
 * The channel below a table: one gutter above, one below, and a row whose
 * height matches the floating selection action bar that shares the space, so
 * a table's count always centres on the bar rather than sitting under it.
 * Any hand-rolled table footer should use this rather than its own padding.
 */
interface DataTablePaginationProps {
  config: PaginationConfig
  totalLabel?: string
  total?: number
  /** Optional status shown at the trailing edge of the footer row. */
  rightSlot?: React.ReactNode
  /** Which container the table is in; see TableFooter. */
  surface?: TableSurface
}

export function DataTablePagination({ config, totalLabel, total, rightSlot, surface }: DataTablePaginationProps) {
  const { currentPage, totalPages, onPageChange } = config

  if (totalPages <= 1 && total === undefined) return null

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page)
    }
  }

  return (
    <TableFooter surface={surface} className="min-[1440px]:grid min-[1440px]:grid-cols-[1fr_auto_1fr]">
      <div className="min-w-0">
        {total !== undefined && totalLabel && (
          <span className="text-sm text-foreground whitespace-nowrap">
            <span className="font-bold">{total}</span> {totalLabel}
          </span>
        )}
      </div>
      <div className="flex-shrink-0">
        {totalPages > 1 && (
          config.variant === 'animated' ? (
            <AnimatedPagination
              currentPage={currentPage}
              totalPages={totalPages}
              goToPage={goToPage}
            />
          ) : (
            <SimplePagination
              currentPage={currentPage}
              totalPages={totalPages}
              goToPage={goToPage}
            />
          )
        )}
      </div>
      <div className="min-w-0 min-[1440px]:text-right min-[1440px]:pr-8">{rightSlot}</div>
    </TableFooter>
  )
}

interface PaginationInnerProps {
  currentPage: number
  totalPages: number
  goToPage: (page: number) => void
}

function SimplePagination({ currentPage, totalPages, goToPage }: PaginationInnerProps) {
  return (
    <div className="flex items-center gap-1 bg-surface-raised rounded-full px-0.5 py-0.5 shadow-lg">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => goToPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Previous
      </Button>

      <span className="text-sm text-content-secondary">
        Page {currentPage} of {totalPages}
      </span>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1"
      >
        Next
        <ChevronRightIcon className="h-4 w-4" />
      </Button>
    </div>
  )
}

function AnimatedPagination({ currentPage, totalPages, goToPage }: PaginationInnerProps) {
  const paginationRef = useRef<HTMLDivElement>(null)
  const pageButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null)
  const [indicatorInitialized, setIndicatorInitialized] = useState(false)
  const prevPageRef = useRef<number>(currentPage)

  useLayoutEffect(() => {
    const updateIndicatorPosition = () => {
      const activeButton = pageButtonRefs.current.get(currentPage)
      const container = paginationRef.current

      if (activeButton && container) {
        const containerRect = container.getBoundingClientRect()
        const buttonRect = activeButton.getBoundingClientRect()

        setIndicatorStyle({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width
        })

        if (!indicatorInitialized) {
          setTimeout(() => setIndicatorInitialized(true), 50)
        }
      }
    }

    prevPageRef.current = currentPage
    updateIndicatorPosition()

    window.addEventListener('resize', updateIndicatorPosition)
    return () => window.removeEventListener('resize', updateIndicatorPosition)
  }, [currentPage, totalPages, indicatorInitialized])

  const renderPageButtons = () => {
    const pages = []

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(renderPageButton(i))
      }
    } else {
      pages.push(renderPageButton(1))

      if (currentPage > 4) {
        pages.push(<span key="ellipsis-start" className="px-2 text-content-subtle">...</span>)
      }

      const startPage = Math.max(2, currentPage - 1)
      const endPage = Math.min(totalPages - 1, currentPage + 1)

      for (let i = startPage; i <= endPage; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(renderPageButton(i))
        }
      }

      if (currentPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end" className="px-2 text-content-subtle">...</span>)
      }

      if (totalPages > 1) {
        pages.push(renderPageButton(totalPages))
      }
    }

    return pages
  }

  const renderPageButton = (page: number) => {
    const isCircular = page < 10
    const isCurrent = page === currentPage

    return (
      <Button
        key={page}
        ref={(el) => { if (el) pageButtonRefs.current.set(page, el) }}
        variant="ghost"
        size="sm"
        onClick={() => goToPage(page)}
        className={`relative z-10 hover:bg-transparent ${isCircular ? 'aspect-square min-w-0 px-0 rounded-full' : 'min-w-[32px]'} ${isCurrent ? 'font-bold' : ''}`}
      >
        {page}
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-1 bg-surface-raised rounded-full px-0.5 py-0.5 shadow-lg">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => goToPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center gap-1"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Previous
      </Button>

      <div ref={paginationRef} className="relative flex items-center gap-1">
        {indicatorStyle && (
          <div
            className="absolute top-0.5 bottom-0.5 bg-active rounded-full"
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width
            }}
          />
        )}
        {renderPageButtons()}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center gap-1"
      >
        Next
        <ChevronRightIcon className="h-4 w-4" />
      </Button>
    </div>
  )
}
